import { useCallback, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Box } from 'foundations-components/transitional/components/Box';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import AwardVisibilityToggleContainer from '../containers/Achievements/AwardVisibilityToggleContainer';
import { EmbedAward, useMaybeCreateOrUpdateAchievement } from '../features/achievement';
import { SendToUpwork } from '../features/upwork';
import { getAchievementPreviewPath, getTrackExamPageUrl } from '../utils/RoutingUtils';
import AwardShareHeader from '../features/achievement/components/AwardShareHeader';
import SocialShareButtons from '../features/achievement/components/SocialShareButtons';
import UsageTracker from '../utils/UsageTracker';
import { NavMarker } from 'react-rhumb';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { useTrackAwardProgress, useTrackProgressById } from '../features/userProgress';
import { View } from 'foundations-components/transitional/components/View';
import { EditorPage } from 'foundations-components/transitional/components/EditorPage';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const AwardShareControls = () => {
  const location = useLocation();
  const {
    trackId
  } = useParams();

  // Determine back navigation path, avoiding award modals to prevent navigation loops
  const getBackNavigationPath = useCallback(() => {
    var _location$state;
    const originalPath = (_location$state = location.state) === null || _location$state === void 0 || (_location$state = _location$state.backFromAwardShare) === null || _location$state === void 0 ? void 0 : _location$state.pathname;

    // exam award modal → exam page (prevents reopening the award modal they just shared from)
    if (originalPath !== null && originalPath !== void 0 && originalPath.includes('/exam/award/')) return getTrackExamPageUrl(trackId);

    // fallback to original path or exam page
    return originalPath !== null && originalPath !== void 0 ? originalPath : getTrackExamPageUrl(trackId);
  }, [location.state, trackId]);
  const {
    trackProgressByIdLoading
  } = useTrackProgressById(Number(trackId));
  const {
    award,
    allProgressLoading
  } = useTrackAwardProgress(Number(trackId));
  const {
    track,
    trackLoading,
    achievementsLoading,
    achievementsError,
    isAwardInvalid
  } = useMaybeCreateOrUpdateAchievement(trackId);
  const anyLoading = achievementsLoading || trackLoading || trackProgressByIdLoading || allProgressLoading;
  const previewPath = getAchievementPreviewPath(trackId);
  useEffect(() => {
    if (track) {
      UsageTracker.track('awardInteraction', {
        action: 'view-sharing-controls',
        trackId,
        trackName: track.name,
        view: 'sharing-controls'
      });
    }
  }, [award, track, trackId]);
  const handleCopyAwardCode = useCallback(() => {
    UsageTracker.track('awardInteraction', {
      action: 'copy-badge-code',
      trackId,
      trackName: track && track.name,
      view: 'sharing-controls'
    });
  }, [track, trackId]);
  if (anyLoading) {
    return /*#__PURE__*/_jsx(LoadingOverlay, {
      "data-test-id": "loading-spinner"
    });
  }
  if (isAwardInvalid) {
    return null;
  }
  return /*#__PURE__*/_jsx(View, {
    minBlockSize: "100vh",
    children: /*#__PURE__*/_jsx(NavMarker, {
      name: "AWARD_SHARE_CONTROLS_LOADED",
      children: /*#__PURE__*/_jsxs(EditorPage, {
        "data-test-id": "award-share-controls",
        headerComponent: /*#__PURE__*/_jsx(AwardShareHeader, {
          backLinkHref: getBackNavigationPath()
        }),
        inset: "0",
        position: "absolute",
        zIndex: 5,
        children: [/*#__PURE__*/_jsx(TFlex, {
          justify: "center",
          marginBlockEnd: "500",
          marginBlockStart: "1300",
          wrap: "wrap",
          children: /*#__PURE__*/_jsx(SocialShareButtons, {
            language: track.language,
            trackId: Number(trackId)
          })
        }), /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          justify: "center",
          marginBlock: "500",
          children: [/*#__PURE__*/_jsx(Box, {
            marginInline: "400",
            children: /*#__PURE__*/_jsx(AwardVisibilityToggleContainer, {
              award: award,
              disabledTooltip: "learningCenter.awardShareControls.disabledToggleTooltip",
              isFetchingAchievements: achievementsLoading,
              language: track.language,
              trackId: trackId
            })
          }), /*#__PURE__*/_jsx(Box, {
            basis: 480,
            shrink: 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.awardShareControls.sharingConsent"
            })
          })]
        }), !achievementsLoading && /*#__PURE__*/_jsx(View, {
          textAlign: "center",
          children: /*#__PURE__*/_jsx(UIRouterLink, {
            "data-test-id": "open-achievement-preview",
            disabled: !!achievementsError,
            to: {
              pathname: previewPath,
              state: location.state
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.awardShareControls.previewCert"
            })
          })
        }), /*#__PURE__*/_jsxs(View, {
          as: "section",
          paddingBlockStart: "1200",
          children: [/*#__PURE__*/_jsx(Flex, {
            borderBlockStartColor: "border-core-default",
            borderBlockStartStyle: "solid",
            borderBlockStartWidth: "100",
            margin: "auto",
            maxInlineSize: "948px",
            paddingBlockEnd: "500",
            paddingBlockStart: "600",
            paddingInline: "0",
            children: /*#__PURE__*/_jsx(EmbedAward, {
              language: track.language,
              onCopyAwardCode: handleCopyAwardCode,
              trackId: Number(trackId)
            })
          }), track && track.upworkCode && /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            justifyContent: "center",
            margin: "auto",
            marginBlockStart: "600",
            children: /*#__PURE__*/_jsx(SendToUpwork, {
              language: track.language,
              trackId: Number(trackId)
            })
          })]
        })]
      })
    })
  });
};
export default AwardShareControls;